<template>
  <div class="clan-chat">
    <div class="chat-header">
      <h3>{{ $t('clan.chat.title') }}</h3>
      <div class="chat-status" v-if="isChatLoading">
        <i class="fas fa-sync fa-spin"></i>
      </div>
    </div>
    
    <div class="chat-messages" ref="messagesContainer">
      <div v-if="chatMessages.length === 0 && !isChatLoading" class="no-messages">
        {{ $t('clan.chat.noMessages') }}
      </div>
      
      <div v-for="message in chatMessages" :key="message.id" class="message">
        <div class="message-header">
          <span class="player-name">{{ message.player?.user?.username || message.player?.username || $t('clan.chat.unknownPlayer') }}</span>
          <span class="message-time">{{ formatTime(message.createdAt) }}</span>
          <button 
            v-if="isPlayerClanLeader" 
            class="delete-button"
            @click="deleteMessage(message.id)"
            :title="$t('clan.chat.deleteMessage')"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <div class="message-body">
          {{ message.text }}
        </div>
      </div>
    </div>
    
    <div class="chat-input">
      <textarea 
        v-model="newMessage" 
        :placeholder="$t('clan.chat.placeholder')"
        :maxlength="maxMessageLength"
        @keyup.enter="onEnterPress"
        ref="messageInput"
      ></textarea>
      <div class="input-footer">
        <span class="char-count" :class="{ 'near-limit': charCountWarning }">
          {{ newMessage.length }}/{{ maxMessageLength }}
        </span>
        <button 
          class="send-button" 
          @click="sendMessage"
          :disabled="!canSendMessage"
        >
          {{ $t('clan.chat.send') }} <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ClanChat',
  data() {
    return {
      newMessage: '',
      maxMessageLength: 450,
      chatUpdateInterval: null,
      CHAT_UPDATE_INTERVAL_SECONDS: 10 // Configurable update interval
    };
  },
  computed: {
    ...mapGetters({
      chatMessages: 'clan/chatMessages',
      isChatLoading: 'clan/isChatLoading',
      chatError: 'clan/chatError',
      isPlayerClanLeader: 'clan/isPlayerClanLeader'
    }),
    canSendMessage() {
      return this.newMessage.trim().length > 0 && !this.isChatLoading;
    },
    charCountWarning() {
      return this.newMessage.length > this.maxMessageLength * 0.9;
    }
  },
  methods: {
    ...mapActions({
      fetchChatMessages: 'clan/fetchClanChatMessages',
      sendClanChatMessage: 'clan/sendClanChatMessage',
      deleteClanChatMessage: 'clan/deleteClanChatMessage'
    }),
    formatTime(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      
      // Today, show only time
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // Within the last week, show day and time
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (date > weekAgo) {
        return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + 
               date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // Older messages, show date
      return date.toLocaleDateString();
    },
    async sendMessage() {
      if (!this.canSendMessage) return;
      
      const message = this.newMessage.trim();
      this.newMessage = '';
      
      const result = await this.sendClanChatMessage(message);
      
      if (!result.success) {
        this.$emit('error', result.error || this.$t('clan.chat.sendError'));
      }
      
      // Focus back on the input
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }
      });
    },
    async deleteMessage(messageId) {
      const result = await this.deleteClanChatMessage(messageId);
      
      if (!result.success) {
        this.$emit('error', result.error || this.$t('clan.chat.deleteError'));
      }
    },
    async refreshMessages() {
      const result = await this.fetchChatMessages();
      
      if (!result.success && result.error) {
        this.$emit('error', result.error);
      }
    },
    startChatUpdates() {
      // Clear any existing interval first
      this.stopChatUpdates();
      
      // Set up new interval
      this.chatUpdateInterval = setInterval(() => {
        this.refreshMessages();
      }, this.CHAT_UPDATE_INTERVAL_SECONDS * 1000);
    },
    stopChatUpdates() {
      if (this.chatUpdateInterval) {
        clearInterval(this.chatUpdateInterval);
        this.chatUpdateInterval = null;
      }
    },
    onEnterPress(event) {
      // Only send message on Enter without shift key
      if (event.shiftKey) return;
      
      event.preventDefault();
      this.sendMessage();
    }
  },
  async created() {
    await this.refreshMessages();
    this.startChatUpdates();
  },
  beforeUnmount() {
    this.stopChatUpdates();
  }
};
</script>

<style scoped>
/* O MURAL do clã (D22-8): mensagens como bilhetes pregados no papel, na língua do mural do Cais —
 * não uma janela escura de chat de aplicativo. A mecânica (polling de 10s, apagar do líder,
 * Enter envia) não mudou. */
.clan-chat {
  display: flex;
  flex-direction: column;
  min-height: 300px;
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  color: var(--tinta);
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--e-2);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.chat-header h3 {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  font-weight: 400;
  color: var(--tinta);
}

.chat-status {
  color: var(--tinta-fraca);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  padding-right: var(--e-1);
}

.no-messages {
  color: var(--tinta-fraca);
  font-style: italic;
  font-size: var(--f-1);
}

/* Cada mensagem é um bilhete no mural: papel frio, alfinetada por uma barra de latão. */
.message {
  background: var(--papel-frio);
  border-left: 3px solid var(--latao);
  padding: var(--e-2);
}

.message-header {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin-bottom: 2px;
}

.player-name {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta);
}

.message-time {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  margin-left: auto;
}

.delete-button {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--tinta-fraca);
}

.message-body {
  font-size: var(--f-1);
  color: var(--tinta);
  overflow-wrap: anywhere;
}

.chat-input {
  margin-top: var(--e-2);
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-2);
}

.chat-input textarea {
  width: 100%;
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
  padding: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--tinta);
  resize: vertical;
  min-height: 56px;
  box-sizing: border-box;
}

.chat-input textarea:focus {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

.input-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--e-1);
}

.char-count {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.char-count.near-limit {
  color: var(--cor-aviso);
}

.send-button {
  border: var(--placa-borda) solid transparent;
  border-image: url('/ui/botao-primario.png') var(--placa-fatia) / var(--placa-borda) repeat;
  background: var(--ouro);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-1) var(--e-3);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--madeira-sombra);
  cursor: pointer;
}

.send-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.send-button:not(:disabled):active {
  border-image-source: url('/ui/botao-primario-apertado.png');
  transform: translateY(2px);
}
</style>
